import ArchiveIcon from '@mui/icons-material/Archive';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import {
  BooleanInput,
  Button,
  Confirm,
  Datagrid,
  DateField,
  FunctionField,
  List,
  TextField,
  useListContext,
  useNotify,
  useRecordContext,
  useRefresh,
} from 'react-admin';
import { commonListSx } from '../components/listStyles';
import { ScrollableDatagrid } from '../components/ScrollableDatagrid';
import { API_URL } from '../constants';
import { httpClient } from '../providers/httpClient';

// openi image-question runs (gs://q4udoc-gen/openi/). "Open review" opens the run's review page
// — the pipeline's own page, served by the backend at its own run.app address, where no one is
// signed in — in a new tab; its Submit stores the kept cards for the run. Seeding is the
// pipeline's seed.yml, run from a terminal with the command the row offers to copy.
//
// The list shows the runs that still need something; a run whose kept cards are all live or taken
// down drops off, as does one an admin archived. "Show finished and archived" brings them back.
// Expanding a submitted run lists its cards; a live one can be taken down, including one the
// review no longer keeps (the run stays listed while such a card is live).

type RunState = 'needs-review' | 'needs-seed' | 'done' | 'archived';
type CardStatus = 'live' | 'pending' | 'taken-down' | 'withheld' | 'not-seeded';

interface KeptCounts {
  kept: number;
  live: number;
  pending: number;
  takenDown: number;
  withheld: number;
  notSeeded: number;
  // Cards of the run that are live although the review no longer keeps them.
  liveNotKept: number;
}

interface ImageRunRecord {
  id: string;
  stem: string;
  submittedAt: string | null;
  submittedBy: string | null;
  submittedCards: number | null;
  archivedAt: string | null;
  cards: KeptCounts | null;
  state: RunState;
}

interface KeptCardRecord {
  id: string;
  topic: string | null;
  stem: string;
  status: CardStatus;
  kept: boolean;
}

const runPath = (run: { id: string }) => `${API_URL}/image-runs/${encodeURIComponent(run.id)}`;

// The server's own reason when it gave one (react-admin's HttpError carries the body's message).
const reasonOf = (cause: unknown, fallback: string) =>
  cause instanceof Error && cause.message ? cause.message : fallback;

const OpenReviewButton = () => {
  const record = useRecordContext<ImageRunRecord>();
  const notify = useNotify();
  if (!record) return null;

  const open = async () => {
    // Opened inside the click so the pop-up blocker allows it, then pointed at the link once the
    // server has minted it.
    const tab = window.open('', '_blank');
    if (!tab) {
      notify('Allow pop-ups for this site to open the review.', { type: 'warning' });
      return;
    }
    try {
      const { json } = await httpClient(`${runPath(record)}/review-link`, { method: 'POST' });
      // The page runs the pipeline's script: give it no handle back to the panel.
      tab.opener = null;
      tab.location.href = json.url;
    } catch (cause) {
      tab.close();
      console.error('image-runs review-link failed:', cause);
      notify(reasonOf(cause, 'Could not open the review'), { type: 'error' });
    }
  };

  return <Button label="Open review" onClick={open} startIcon={<OpenInNewIcon />} />;
};

// An atlas bank seeds at full resolution so plate labels stay sharp (seed.yml's max_edge input).
const seedCommand = (run: ImageRunRecord) =>
  `gh workflow run seed.yml --repo Q4uDoc/question_gen_openi -f run_id=${run.id}${run.stem === 'atlas' ? ' -f max_edge=0' : ''}`;

const SeedCommand = () => {
  const run = useRecordContext<ImageRunRecord>();
  const notify = useNotify();
  // Only while a kept card is left to seed or publish: seeding again changes nothing else.
  if (!run?.submittedAt || (run.cards && run.cards.pending + run.cards.notSeeded === 0))
    return null;
  const command = seedCommand(run);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      notify(
        'Seed command copied. Paste it in a terminal; refresh this list when the run is done.',
      );
    } catch {
      notify('Could not copy. Select the command and copy it instead.', { type: 'warning' });
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box component="code" sx={{ fontSize: '0.8em', wordBreak: 'break-all' }}>
        {command}
      </Box>
      <Button label="Copy" onClick={copy} startIcon={<ContentCopyIcon />} />
    </Box>
  );
};

const countsText = (cards: KeptCounts) =>
  [
    `${cards.live} of ${cards.kept} live`,
    cards.pending > 0 && `${cards.pending} pending`,
    cards.takenDown > 0 && `${cards.takenDown} taken down`,
    cards.withheld > 0 && `${cards.withheld} withheld (PHI)`,
    cards.liveNotKept > 0 && `${cards.liveNotKept} live but no longer kept`,
  ]
    .filter(Boolean)
    .join(' · ');

const statusText = (run: ImageRunRecord) => {
  const counts = run.cards ? countsText(run.cards) : null;
  switch (run.state) {
    case 'needs-review':
      return 'Needs review';
    case 'needs-seed': {
      if (!run.cards) return 'Submitted · card status unavailable';
      const { live, pending, takenDown, notSeeded, liveNotKept } = run.cards;
      const label =
        liveNotKept > 0 && pending + notSeeded === 0
          ? 'Needs a take-down'
          : live + pending + takenDown > 0
            ? 'Not all live'
            : 'Ready to seed';
      return `${label} · ${counts}`;
    }
    case 'done':
      return `Done · ${counts}`;
    case 'archived':
      return counts ? `Archived · ${counts}` : 'Archived';
  }
};

const ArchiveButton = () => {
  const run = useRecordContext<ImageRunRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  const { filterValues } = useListContext();
  const [busy, setBusy] = useState(false);
  if (!run) return null;
  const archived = run.state === 'archived';
  const showingHidden = filterValues?.showAll === true;

  const toggle = async () => {
    setBusy(true);
    try {
      await httpClient(`${runPath(run)}/${archived ? 'unarchive' : 'archive'}`, { method: 'POST' });
      notify(
        archived
          ? `${run.id} is back in the list`
          : showingHidden
            ? `${run.id} archived`
            : `${run.id} archived. Turn on "Show finished and archived" to see it again.`,
      );
      refresh();
    } catch (cause) {
      console.error('image-runs archive failed:', cause);
      notify(reasonOf(cause, 'Could not update the run'), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      label={archived ? 'Unarchive' : 'Archive'}
      onClick={toggle}
      disabled={busy}
      startIcon={archived ? <UnarchiveIcon /> : <ArchiveIcon />}
    />
  );
};

const CARD_STATUS: Record<CardStatus, { label: string; color: 'success' | 'warning' | 'default' }> =
  {
    live: { label: 'Live', color: 'success' },
    pending: { label: 'Pending (hidden)', color: 'warning' },
    'taken-down': { label: 'Taken down', color: 'default' },
    withheld: { label: 'Withheld (PHI)', color: 'default' },
    'not-seeded': { label: 'Not seeded', color: 'default' },
  };

// A card the review no longer keeps is listed only when it is live (to take down) or taken down.
const chipOf = (card: KeptCardRecord) => {
  const chip = CARD_STATUS[card.status];
  if (card.kept) return chip;
  return {
    label: `${chip.label} · no longer kept`,
    color: card.status === 'live' ? ('warning' as const) : chip.color,
  };
};

const TakeDownButton = ({
  runId,
  card,
  onDone,
}: {
  runId: string;
  card: KeptCardRecord;
  onDone: () => void;
}) => {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Only a live card: a pending one could be flipped live by a running seed's publish step right
  // after it was taken down. To keep a pending card out, resubmit the review without it.
  if (card.status !== 'live') return null;

  const takeDown = async () => {
    setBusy(true);
    try {
      await httpClient(`${runPath({ id: runId })}/cards/${encodeURIComponent(card.id)}/take-down`, {
        method: 'POST',
      });
      notify(`${card.id} taken down`);
      setOpen(false);
      onDone();
    } catch (cause) {
      console.error('image-runs take-down failed:', cause);
      notify(reasonOf(cause, 'Could not take the card down'), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        label="Take down"
        onClick={() => setOpen(true)}
        disabled={busy}
        color="error"
        startIcon={<VisibilityOffIcon />}
      />
      <Confirm
        isOpen={open}
        loading={busy}
        fullWidth
        title="Take this card down?"
        content={`${card.id} is soft-deleted, like a question removed from the report queue: hidden from feeds, search and new quizzes straight away. Seeding the run again will not bring it back, so this cannot be undone from the panel.`}
        confirm="Take down"
        confirmColor="warning"
        onConfirm={takeDown}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

// A submitted run's cards, fetched when its row is expanded: the kept ones, then any the review no
// longer keeps that went live anyway (a resubmit that raced a seed) or were taken down since.
const RunCards = () => {
  const run = useRecordContext<ImageRunRecord>();
  const refresh = useRefresh();
  const [cards, setCards] = useState<KeptCardRecord[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [version, setVersion] = useState(0);
  const runId = run?.id;
  // The list's own refresh (or a resubmit) changes these, and reloads the cards with them.
  const runKey = run ? `${run.submittedAt}|${JSON.stringify(run.cards)}` : '';

  // biome-ignore lint/correctness/useExhaustiveDependencies: `runKey` and `version` are reload triggers.
  useEffect(() => {
    if (!runId) return;
    let active = true;
    setFailed(false);
    httpClient(`${runPath({ id: runId })}/cards`)
      .then(({ json }) => {
        if (active) setCards(json);
      })
      .catch((cause) => {
        console.error('image-runs cards failed:', cause);
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [runId, runKey, version]);

  if (!runId) return null;
  if (failed) return <Typography color="error">Could not load this run's cards.</Typography>;
  if (!cards) return <Typography color="text.secondary">Loading cards…</Typography>;

  const reload = () => {
    setVersion((v) => v + 1);
    refresh();
  };

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Card</TableCell>
          <TableCell>Topic</TableCell>
          <TableCell>Question</TableCell>
          <TableCell>Status</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {cards.map((card) => (
          <TableRow key={card.id}>
            <TableCell>
              <code>{card.id}</code>
            </TableCell>
            <TableCell>{card.topic ?? '—'}</TableCell>
            <TableCell sx={{ maxWidth: 420 }}>
              {card.stem.length > 140 ? `${card.stem.slice(0, 140)}…` : card.stem}
            </TableCell>
            <TableCell>
              <Chip size="small" {...chipOf(card)} />
            </TableCell>
            <TableCell>
              <TakeDownButton runId={runId} card={card} onDone={reload} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const runFilters = [
  <BooleanInput key="showAll" source="showAll" label="Show finished and archived" alwaysOn />,
];

export const ImageRunList = () => (
  // empty={false}: react-admin's empty page has no filters, so with nothing left to do the
  // "Show finished and archived" toggle would vanish with it.
  <List sx={commonListSx} exporter={false} filters={runFilters} empty={false}>
    <ScrollableDatagrid>
      <Datagrid
        bulkActionButtons={false}
        rowClick={false}
        // The component, not an element: react-admin tells them apart with react-is, which here
        // resolves to React 19's copy and does not recognise this bundle's React 18 elements.
        expand={RunCards}
        isRowExpandable={(run) => !!run.submittedAt}
      >
        <TextField source="id" label="Run" sortable={false} />
        <DateField source="generatedAt" label="Generated" showTime sortable={false} />
        <FunctionField<ImageRunRecord>
          label="Submitted"
          render={(run) =>
            run.submittedAt
              ? `${run.submittedCards ?? '?'} cards · ${new Date(run.submittedAt).toLocaleString()}`
              : '—'
          }
        />
        <FunctionField<ImageRunRecord> label="Status" render={statusText} />
        <FunctionField label="Seed" render={() => <SeedCommand />} />
        <OpenReviewButton />
        <ArchiveButton />
      </Datagrid>
    </ScrollableDatagrid>
  </List>
);
